import type { GASetupDatalayerParamCart } from '@integration-layer/schemas/GAEvents/dataLayerSetup'

/**
 * Composable for preparing cart parameters for Google Analytics tracking.
 *
 * Calculates various cart-related metrics such as:
 * - Total number of products
 * - Cart type (mono-brand or multi-brand)
 * - Product count per brand
 * - Total cart value
 *
 * @returns {GASetupDatalayerParamCart} Cart tracking parameters
 */
export const useParamCart = (): GASetupDatalayerParamCart => {
  /**
   * Destructure cart-related values from useCart composable.
   * @type {Object}
   */
  const { cartId, lineItems, totalAmoutOrder } = useCart()

  /**
   * Total number of products in the cart.
   * @type {number}
   */
  const cartProducts = lineItems.value.length

  /**
   * Brand metadata constants for different Armani brands.
   * @type {string}
   */
  const GA_METADATA = 'giorgio-armani'
  const EA_METADATA = 'emporio-armani'
  const EA7_METADATA = 'ea7'
  const AX_METADATA = 'armani-exchange'

  /**
   * Computed first brand in the cart.
   * @type {ComputedRef<string | undefined>}
   */
  const firstBrand = computed(() => lineItems.value?.[0].metadata?.website)

  /**
   * Count of products per brand in the cart.
   * @type {number}
   */
  const axProducts = lineItems.value.filter(
    el => el.metadata?.website === AX_METADATA
  ).length
  const gaProducts = lineItems.value.filter(
    el => el.metadata?.website === GA_METADATA
  ).length
  const eaProducts = lineItems.value.filter(
    el => el.metadata?.website === EA_METADATA
  ).length
  const ea7Products = lineItems.value.filter(
    el => el.metadata?.website === EA7_METADATA
  ).length

  /**
   * Determines if the cart contains products from a single brand.
   * @type {ComputedRef<boolean>}
   */
  const isMonoBrand = computed(() =>
    lineItems.value.every(el => el.metadata?.website === firstBrand.value)
  )

  /**
   * Calculates total cart value, handling discounts and fallback scenarios.
   * @type {number}
   */
  const allPrices = lineItems.value.map(el => {
    if (!el.total_amount_cents) return 0
    if (!el.discount_cents) return el.total_amount_cents
    return el.total_amount_cents
  })
  const cartTotalValue = allPrices.reduce((acc, el) => (acc += el), 0) / 100

  /**
   * Formats cart total value, preferring totalAmoutOrder if available.
   * @type {string}
   */
  const formattedCartTotal =
    totalAmoutOrder.value?.toFixed(2) ?? cartTotalValue.toFixed(2)

  /**
   * Returns comprehensive cart tracking parameters.
   * @returns {GASetupDatalayerParamCart} Cart tracking metadata
   */
  return {
    /** Unique cart identifier */
    cartID: cartId.value ?? '',
    /** Total number of products in cart */
    cart_products: cartProducts,
    /** Cart brand type (mono or multi) */
    cart_type: isMonoBrand.value ? 'mono_brand' : 'multi_brand',
    /** Number of Armani Exchange products */
    armani_exchange_cart: axProducts,
    /** Client service cart (placeholder, to be implemented) */
    client_service_cart: 0,
    /** Number of EA7 products */
    ea7_cart: ea7Products,
    /** Number of Emporio Armani products */
    emporio_armani_cart: eaProducts,
    /** Number of Giorgio Armani products */
    giorgio_armani_cart: gaProducts,
    /** Total cart value */
    cartTotalValue: formattedCartTotal,
  }
}
